import { asyncTimeout } from '@caresend/utils';
import { defineComponent, ref, watch, computed, onMounted } from 'vue';
import __vue_component__ from '../../../../layout/Header/Header.vue.js';
import __vue_component__$2 from './components/Navigator/Navigator.vue.js';
import __vue_component__$1 from './components/PortalMap/PortalMap.vue.js';
import __vue_component__$3 from '../ShiftDetails/ShiftDetailsOverlay.vue.js';
import { useItineraryGeoSearch } from '../useItineraryGeoSearch.js';
import __vue_component__$6 from '../WaypointDetails/WaypointDetails.vue.js';
import { usePortalStore } from '../../store/portal/index.js';
import { useItineraryWorkflowsStore } from '../../store/portal/workflow/itinerary.js';
import __vue_component__$4 from '../../../../transitions/TransitionDownUp/TransitionDownUp.vue.js';
import __vue_component__$5 from '../../../../transitions/TransitionFadeOutIn/TransitionFadeOutIn.vue.js';
import { useMediaQuery, useElementChildren } from '../../../../../composables/dom.js';
import { getStore } from '../../../../../store/helpers/initStore.js';
import { getRoute, getPrevRoute, getRouter } from '../../../../../router/helpers/initRouter.js';
import '@caresend/types';
import 'immutability-helper';
import '../../../../../router/routes/dev/model.js';
import '../../../../../router/routes/dev/routes.js';
import '../../../../../router/routes/dashboard/model.js';
import '../../../../../router/routes/pro/model.js';
import '../../../../../router/routes/visitsPage/model.js';
import '../../../../../router/routes/orderFlow/model.js';
import '../../../../../router/routes/orderFlow/privateHelpers.js';
import '../../../../../router/routes/orderFlow/routes.js';
import '../../../../../router/routes/schedulingFlow/model.js';
import '../../../../../router/routes/schedulingFlow/routes.js';
import '../../../../../router/routes/chartingFlow/model.js';
import '../../../../../router/routes/chartingFlow/routes.js';
import '../../../../../router/routes/portal/portalRoutes.js';
import '../../../../../router/routes/portal/model.js';
import '../../../../../router/routes/visitsPage/routes.js';

var script = defineComponent({
    name: 'Portal',
    components: {
        Header: __vue_component__,
        PortalMap: __vue_component__$1,
        Navigator: __vue_component__$2,
        ShiftDetailsOverlay: __vue_component__$3,
        TransitionDownUp: __vue_component__$4,
        TransitionFadeOutIn: __vue_component__$5,
        WaypointDetails: __vue_component__$6,
    },
    props: {
        customRouteOptions: {
            type: Array,
            default: () => [],
        },
        authActions: {
            type: Array,
            default: () => [],
        },
        colorScheme: {
            type: String,
            default: 'light',
        },
        itineraryID: {
            type: String,
            default: '',
        },
        waypointID: {
            type: String,
            default: null,
        },
    },
    setup(props) {
        const currentRoute = getRoute();
        const portalStore = usePortalStore();
        const store = getStore();
        const { viewAs } = portalStore;
        const itineraryWorkflowsStore = useItineraryWorkflowsStore();
        const itineraryGeoSearch = useItineraryGeoSearch();
        const modalWaypointID = ref(props.waypointID);
        watch(() => props.waypointID, (newVal) => {
            modalWaypointID.value = newVal;
        });
        watch(() => itineraryWorkflowsStore.state.value.waypointID, (newVal) => {
            modalWaypointID.value = newVal;
        });
        const { setCoordLimits, toggleShowMap, } = itineraryGeoSearch;
        const mapHighlightID = computed(() => itineraryGeoSearch.mapHighlightID);
        const dispatchMapLegend = computed(() => itineraryGeoSearch.dispatchMapLegend);
        /** Unused */
        const isMapLoading = ref(true);
        const setMapLoading = (loading) => {
            isMapLoading.value = loading;
        };
        const itineraryViewContainer = ref(null);
        const itinerary = computed(() => portalStore.itinerary.value);
        /** Not really fleshed out */
        watch(() => props.colorScheme, () => {
            portalStore.state.set.colorScheme(props.colorScheme);
        });
        const { matches: isMobile } = useMediaQuery('mobile');
        /**
         * Get the next waypoint in transit
         * TODO: implement a more generic way to pop the scroll position to the map view and back
         */
        const nextInTransitWaypoint = computed(() => portalStore.itinerary.getters.nextInTransitWaypoint());
        /**
         * There are two cases where the scroll position should be automatically adjusted:
         * 1. A waypoint in the mounted itinerary changes to `inTransit`
         * 2. The user triggers a navigator route change
         */
        watch([
            nextInTransitWaypoint,
            () => currentRoute.value.name,
        ], ([next, prev], [nextRouteName, prevRouteName]) => {
            var _a, _b;
            if (!isMobile.value)
                return;
            if (!itineraryViewContainer.value)
                return;
            if ((next && !prev && viewAs.nurse)) {
                (_a = itineraryViewContainer.value) === null || _a === void 0 ? void 0 : _a.scrollTo(0, -10000);
            }
            else if ((!next && prev && viewAs.nurse)
                || (nextRouteName !== prevRouteName)) {
                (_b = itineraryViewContainer.value) === null || _b === void 0 ? void 0 : _b.scrollTo(0, 10000);
            }
        });
        const showMap = computed(() => itineraryGeoSearch.showMap);
        /** Based on instructions regarding map display, override the content width */
        const contentWidthClasses = computed(() => {
            var _a, _b, _c, _d;
            const classes = [];
            if ((!showMap.value && ((_b = (_a = currentRoute.value.meta) === null || _a === void 0 ? void 0 : _a.portal) === null || _b === void 0 ? void 0 : _b.lockMap) !== 'collapsed')
                || ((_d = (_c = currentRoute.value.meta) === null || _c === void 0 ? void 0 : _c.portal) === null || _d === void 0 ? void 0 : _d.lockMap) === 'full') {
                classes.push('Portal__itinerary-panel--full-width');
            }
            return classes;
        });
        const appRouteLoading = computed(() => store.state.app.routeLoading);
        /**
         * If the scroll position is less than this threshold, set it to 0 to
         * invoke the "detached" pane behavior
         */
        const scrollPositionThreshold = 16;
        const mapFocused = ref(false);
        /** Ensure the data-scroll attribute is set for CSS scroll position based configuration */
        onMounted(() => {
            const portal = document.querySelector('.Portal');
            if (portal) {
                portal.setAttribute('data-scroll', portal.scrollTop.toString());
                portal.addEventListener('scroll', () => {
                    let { scrollTop } = portal;
                    const deviceHeight = window.innerHeight;
                    if (scrollTop > -scrollPositionThreshold) {
                        scrollTop = 0;
                    }
                    /**
                     * Since the structure of the mobile map/router panel is a *scroll-up*
                     * gesture, scrollTop values will always be negative relative to the base
                     * state.
                     *
                     * Webkit browsers are the only ones which ever report a scrollTop
                     * value greater than 0. This ends up causing the panel to stick in an
                     * incorrect scroll position temporarily creating a jarring experience.
                     * The following enforces this to never happen.
                     */
                    if (scrollTop > 0) {
                        portal.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        });
                    }
                    if (scrollTop < -deviceHeight * 0.6 && isMobile.value)
                        mapFocused.value = true;
                    else
                        mapFocused.value = false;
                    portal.setAttribute('data-scroll', scrollTop.toString());
                });
            }
        });
        const prevRoute = getPrevRoute();
        const router = getRouter();
        const route = getRoute();
        const headingValues = computed(() => {
            var _a, _b;
            if (!route.value)
                return;
            return (_b = (_a = route.value.meta) === null || _a === void 0 ? void 0 : _a.heading) === null || _b === void 0 ? void 0 : _b.call(_a, {
                route: route.value,
                prevRoute: prevRoute.value,
                store,
                router,
            }).value;
        });
        /**
         * This solution allows the header to be potentially unmounted.
         * In the event the header is mounted, the headerChildren value updates and
         * the header height is provided to the innerStyles computed value to apply
         * padding to the panel inner content. If the size of the header changes (isMobile
         * changes), the header height is updated accordingly.
         */
        const headerHeight = ref(0);
        const headerChildren = useElementChildren(itineraryViewContainer, ['.Portal__header']);
        watch([
            () => headerChildren.children.value['.Portal__header'],
            () => headingValues.value,
            isMobile,
        ], async () => {
            // Allow DOM to complete resize
            await asyncTimeout(300);
            const header = document.querySelector('.Portal__header');
            if (!header || !headingValues.value) {
                headerHeight.value = 0;
                return;
            }
            headerHeight.value = header.clientHeight;
        }, {
            immediate: true,
        });
        const innerStyles = computed(() => {
            var _a, _b;
            const styles = {};
            if (headerHeight.value) {
                const padding = ((_b = (_a = route.value.meta) === null || _a === void 0 ? void 0 : _a.portal) === null || _b === void 0 ? void 0 : _b.noPanelPadding) ? 0 : 12;
                styles.paddingTop = `${headerHeight.value + padding}px !important`;
            }
            return styles;
        });
        const currentRouteName = computed(() => { var _a, _b; return (_b = (_a = route.value) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : undefined; });
        return {
            appRouteLoading,
            contentWidthClasses,
            currentRouteName,
            dispatchMapLegend,
            headingValues,
            innerStyles,
            isMapLoading,
            itinerary,
            itineraryViewContainer,
            mapHighlightID,
            modalWaypointID,
            setCoordLimits,
            setMapLoading,
            showMap,
            toggleShowMap,
            mapFocused,
        };
    },
});

export { script as default };
